// src/components/ui/forms/ServiceDetailsForm/ServiceDetailsFormShadcn.jsx
import React, { useState, useEffect } from 'react';
import {
  Palette,
  Type,
  FileText
} from 'lucide-react';

// shadcn components
import { Input } from '../../input';
import { Label } from '../../label';
import { Textarea } from '../../textarea';
import { Switch } from '../../switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../card';

import { cn } from '../../../../utils/cn';

const ServiceDetailsFormShadcn = ({
  service,
  onUpdate,
  onUnsavedChanges,
  saving = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color_code: '#8B5CF6',
    active: true,
    requires_confirmation: false,
    ...service
  });

  const [lastSavedData, setLastSavedData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // Color options
  const colorOptions = [
    '#8B5CF6', // Purple
    '#06B6D4', // Cyan
    '#10B981', // Emerald
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#EC4899', // Pink
    '#8B5A2B', // Brown
    '#6366F1'  // Indigo
  ];

  // Update form data when service changes
  useEffect(() => {
    if (service) {
      const newFormData = {
        name: service.name || '',
        description: service.description || '',
        color_code: service.color_code || '#8B5CF6',
        active: service.active !== false,
        requires_confirmation: service.requires_confirmation || false
      };
      setFormData(newFormData);
      setLastSavedData(newFormData);
      setValidationErrors({});
    }
  }, [service]);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(lastSavedData);
    onUnsavedChanges(hasChanges);
  }, [formData, lastSavedData, onUnsavedChanges]);

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle field blur (auto-save)
  const handleFieldBlur = async (field) => {
    // Only save if there are changes and no validation errors
    if (formData[field] !== lastSavedData[field] && !validationErrors[field]) {
      await handleSave();
    }
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = 'Service name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const updateData = {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        color_code: formData.color_code,
        active: formData.active,
        requires_confirmation: formData.requires_confirmation
      };

      await onUpdate(service.id, updateData);
      setLastSavedData(formData);
    } catch (error) {
      console.error('Failed to save service:', error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>
            Update the service's core details and description
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Service Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <Type className="w-4 h-4" />
              Service Name
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              onBlur={() => handleFieldBlur('name')}
              placeholder="Enter service name..."
              className={cn(
                validationErrors.name && "border-red-500 focus-visible:ring-red-500"
              )}
              required
            />
            {validationErrors.name && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <span className="w-4 h-4 rounded-full bg-red-500/20 flex items-center justify-center text-xs">!</span>
                {validationErrors.name}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Description
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              onBlur={() => handleFieldBlur('description')}
              placeholder="Describe what this service includes..."
              rows={3}
              className="resize-none"
            />
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>
            Choose a color to identify this service in your system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Service Color
            </Label>
            <div className="flex gap-2 flex-wrap">
              {colorOptions.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleFieldChange('color_code', color)}
                  className={cn(
                    "w-8 h-8 rounded-lg border-2 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary/50",
                    formData.color_code === color
                      ? "border-primary scale-110 shadow-lg"
                      : "border-border hover:border-muted-foreground"
                  )}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Service Settings</CardTitle>
          <CardDescription>
            Configure availability and booking requirements
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Active Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-base font-medium">Service Active</Label>
              <p className="text-sm text-muted-foreground">Make this service available for booking</p>
            </div>
            <Switch
              checked={formData.active}
              onCheckedChange={(checked) => {
                handleFieldChange('active', checked);
                // Auto-save for toggles since they're discrete changes
                setTimeout(() => handleSave(), 100);
              }}
            />
          </div>

          {/* Requires Confirmation Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-base font-medium">Requires Confirmation</Label>
              <p className="text-sm text-muted-foreground">Appointments need manual approval before booking</p>
            </div>
            <Switch
              checked={formData.requires_confirmation}
              onCheckedChange={(checked) => {
                handleFieldChange('requires_confirmation', checked);
                // Auto-save for toggles since they're discrete changes
                setTimeout(() => handleSave(), 100);
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceDetailsFormShadcn;